// src/AdminPanel/ManageCategories.js
import React, { useState, useEffect } from "react";
import { useSale } from "../contexts/SaleContext";
import toast from "../utils/toast";
import { db } from "../firebase/firebase";
import { collection, getDocs, writeBatch, query, where } from "firebase/firestore";

// ── Behavior options ────────────────────────────────────────────────────────
const BEHAVIOR_OPTIONS = [
    {
        value: "standard",
        label: "Standard Unit",
        description: "Sold as a complete unit — Syrup, Cream, Drops, Injection, Sachet",
        icon: "fa-box",
        color: "#1565c0",
        bg: "#e8f0fe",
    },
    {
        value: "medicine",
        label: "Tablet / Capsule",
        description: "Has strips & tablets — supports pack and strip pricing",
        icon: "fa-pills",
        color: "#d2222d",
        bg: "#fff0f1",
    },
    {
        value: "weight",
        label: "Weight / Volume",
        description: "Sold by weight or volume — Dairy, Nutritional Supplements, Powders",
        icon: "fa-weight-scale",
        color: "#2e7d32",
        bg: "#e8f5e9",
    },
];

const ManageCategories = () => {
    const { categories, categoriesRaw, saveCategories, categoryDiscounts, saveCategoryDiscounts } = useSale();

    const [newCategoryName, setNewCategoryName] = useState("");
    const [newCategoryBehavior, setNewCategoryBehavior] = useState("standard");
    const [showAddModal, setShowAddModal] = useState(false);

    const [editingCategory, setEditingCategory] = useState(null); // old name string
    const [editCategoryName, setEditCategoryName] = useState("");

    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

    // Confirmation modal: { type: "delete"|"rename", cat, newName? }
    const [confirmModal, setConfirmModal] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    // ── Derived lists ──────────────────────────────────────────────────────────
    const filteredRaw = categoriesRaw.filter((item) =>
        item.name.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => { setCurrentPage(1); }, [search, pageSize]);

    const totalPages = Math.max(1, Math.ceil(filteredRaw.length / pageSize));
    const paginated = filteredRaw.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    // ── Add ────────────────────────────────────────────────────────────────────
    const handleAddCategory = async () => {
        const name = newCategoryName.trim();
        if (!name) return;
        if (categories.includes(name)) {
            toast.error("Category already exists");
            return;
        }
        try {
            const newObj = { name, behavior: newCategoryBehavior };
            await saveCategories([...categoriesRaw, newObj]);
            setNewCategoryName("");
            setNewCategoryBehavior("standard");
            setShowAddModal(false);
            toast.success(`Category "${name}" added`);
        } catch (err) {
            console.error(err);
            toast.error("Failed to add category");
        }
    };

    // ── Delete (show confirmation) ─────────────────────────────────────────────
    const handleDeleteCategory = (cat) => {
        setConfirmModal({ type: "delete", cat });
    };

    // ── Delete (execute) ──────────────────────────────────────────────────────
    const executeDelete = async () => {
        const { cat } = confirmModal;
        setConfirmModal(null);
        setIsProcessing(true);
        try {
            const newList = categoriesRaw.filter((item) => item.name !== cat);
            await saveCategories(newList);

            // Clear any discount tied to this category
            if (categoryDiscounts[cat] !== undefined) {
                const updated = { ...categoryDiscounts };
                delete updated[cat];
                await saveCategoryDiscounts(updated);
            }

            // Batch-clear the type field on all products that had this category
            const q = query(collection(db, "products"), where("type", "==", cat));
            const snapshot = await getDocs(q);
            if (snapshot.docs.length > 0) {
                const batch = writeBatch(db);
                snapshot.docs.forEach((d) => batch.update(d.ref, { type: "" }));
                await batch.commit();
                toast.success(`Category "${cat}" deleted — ${snapshot.docs.length} product(s) updated`);
            } else {
                toast.success(`Category "${cat}" deleted`);
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to delete category");
        } finally {
            setIsProcessing(false);
        }
    };

    // ── Rename (start inline edit) ─────────────────────────────────────────────
    const startEdit = (name) => {
        setEditingCategory(name);
        setEditCategoryName(name);
    };

    // ── Rename (capture intent → show confirmation) ────────────────────────────
    const requestSaveEdit = () => {
        const newName = editCategoryName.trim();
        if (!newName || newName === editingCategory) {
            setEditingCategory(null);
            return;
        }
        if (categories.includes(newName)) {
            toast.error("A category with this name already exists");
            return;
        }
        const oldName = editingCategory;
        setEditingCategory(null);
        setConfirmModal({ type: "rename", cat: oldName, newName });
    };

    // ── Rename (execute) ──────────────────────────────────────────────────────
    const executeRename = async () => {
        const { cat, newName } = confirmModal;
        setConfirmModal(null);
        setIsProcessing(true);
        try {
            // Update categories list preserving behavior
            const newList = categoriesRaw.map((item) =>
                item.name === cat ? { ...item, name: newName } : item
            );
            await saveCategories(newList);

            // Move discount to new name
            if (categoryDiscounts[cat] !== undefined) {
                const updated = { ...categoryDiscounts };
                updated[newName] = updated[cat];
                delete updated[cat];
                await saveCategoryDiscounts(updated);
            }

            // Batch-update products with the old category name
            const q = query(collection(db, "products"), where("type", "==", cat));
            const snapshot = await getDocs(q);
            if (snapshot.docs.length > 0) {
                const batch = writeBatch(db);
                snapshot.docs.forEach((d) => batch.update(d.ref, { type: newName }));
                await batch.commit();
                toast.success(`Renamed to "${newName}" — ${snapshot.docs.length} product(s) updated`);
            } else {
                toast.success(`Category renamed to "${newName}"`);
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to rename category");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleConfirm = () => {
        if (!confirmModal) return;
        if (confirmModal.type === "delete") executeDelete();
        else if (confirmModal.type === "rename") executeRename();
    };

    // ── Close add modal helper ─────────────────────────────────────────────────
    const closeAddModal = () => {
        setShowAddModal(false);
        setNewCategoryName("");
        setNewCategoryBehavior("standard");
    };

    // ── Behavior badge helper ──────────────────────────────────────────────────
    const BehaviorBadge = ({ behavior }) => {
        const opt = BEHAVIOR_OPTIONS.find((o) => o.value === behavior) || BEHAVIOR_OPTIONS[0];
        return (
            <span style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                fontSize: 11, fontWeight: 700, padding: "3px 9px",
                borderRadius: 20, background: opt.bg, color: opt.color,
                whiteSpace: "nowrap",
            }}>
                <i className={`fa-solid ${opt.icon}`}></i>
                {opt.label}
            </span>
        );
    };

    return (
        <div className="upload-page">

            {/* ── Confirmation Modal ── */}
            {confirmModal && (
                <div className="confirm-overlay">
                    <div className="confirm-box">
                        <div className="confirm-icon">
                            <i className={`fa-solid ${confirmModal.type === "delete" ? "fa-triangle-exclamation" : "fa-pen-to-square"}`}></i>
                        </div>
                        <h3>{confirmModal.type === "delete" ? "Delete Category?" : "Rename Category?"}</h3>
                        <p>
                            {confirmModal.type === "delete" ? (
                                <>Are you sure you want to delete <strong>"{confirmModal.cat}"</strong>?<br />
                                All products assigned this category will have their category cleared.</>
                            ) : (
                                <>Rename <strong>"{confirmModal.cat}"</strong> to <strong>"{confirmModal.newName}"</strong>?<br />
                                All products with this category will be updated in Firebase.</>
                            )}
                        </p>
                        <div className="confirm-actions">
                            <button
                                className={confirmModal.type === "delete" ? "confirm-delete-btn" : "spd-save-btn"}
                                onClick={handleConfirm}
                                disabled={isProcessing}
                            >
                                {confirmModal.type === "delete"
                                    ? <><i className="fa-solid fa-trash"></i> Yes, Delete</>
                                    : <><i className="fa-solid fa-check"></i> Yes, Rename</>}
                            </button>
                            <button className="cancel-btn" onClick={() => setConfirmModal(null)} disabled={isProcessing}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Add Category Modal ── */}
            {showAddModal && (
                <div
                    className="confirm-overlay"
                    onClick={(e) => { if (e.target === e.currentTarget) closeAddModal(); }}
                >
                    <div className="confirm-box" style={{ maxWidth: 500, width: "90%" }}>
                        <div className="confirm-icon" style={{ background: "#e8f0fe", color: "#1565c0" }}>
                            <i className="fa-solid fa-tag"></i>
                        </div>
                        <h3 style={{ marginBottom: 6 }}>New Category</h3>
                        <p style={{ marginBottom: 18, color: "#888", fontSize: 13 }}>
                            Enter a name and choose a behavior type for the category.
                        </p>

                        {/* Name input */}
                        <input
                            type="text"
                            className="spd-number-input"
                            style={{ width: "100%", marginBottom: 18, fontWeight: 600, boxSizing: "border-box" }}
                            placeholder="Category name…"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleAddCategory()}
                            autoFocus
                        />

                        {/* Behavior selector */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 22, textAlign: "left" }}>
                            {BEHAVIOR_OPTIONS.map((opt) => {
                                const selected = newCategoryBehavior === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setNewCategoryBehavior(opt.value)}
                                        style={{
                                            display: "flex", alignItems: "center", gap: 12,
                                            padding: "10px 14px", borderRadius: 8, cursor: "pointer",
                                            fontFamily: "inherit", fontSize: 13, fontWeight: 600,
                                            border: `2px solid ${selected ? opt.color : "#e0e0e0"}`,
                                            background: selected ? opt.bg : "#fafafa",
                                            color: selected ? opt.color : "#555",
                                            transition: "all 0.15s ease",
                                            outline: "none",
                                            textAlign: "left",
                                        }}
                                    >
                                        <i
                                            className={`fa-solid ${opt.icon}`}
                                            style={{ fontSize: 16, width: 20, textAlign: "center" }}
                                        ></i>
                                        <span style={{ flex: 1 }}>
                                            {opt.label}
                                            <span style={{
                                                display: "block", fontSize: 11, fontWeight: 400,
                                                color: selected ? opt.color : "#999", marginTop: 2,
                                            }}>
                                                {opt.description}
                                            </span>
                                        </span>
                                        {selected && (
                                            <i className="fa-solid fa-circle-check" style={{ fontSize: 16 }}></i>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="confirm-actions">
                            <button
                                className="spd-save-btn"
                                style={{ padding: "10px 24px" }}
                                onClick={handleAddCategory}
                                disabled={!newCategoryName.trim()}
                            >
                                <i className="fa-solid fa-plus" style={{ marginRight: 6 }}></i>Create
                            </button>
                            <button className="cancel-btn" onClick={closeAddModal}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="spd-section-card">
                {/* ── Heading row ── */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <h2 className="spd-section-heading" style={{ margin: 0 }}>Manage Categories</h2>
                    <button
                        className="spd-save-btn"
                        style={{ padding: "9px 18px", display: "flex", alignItems: "center", gap: 7 }}
                        onClick={() => setShowAddModal(true)}
                    >
                        <i className="fa-solid fa-plus"></i>
                        Add Category
                    </button>
                </div>
                <p className="admin-page-subtitle">
                    Add, rename, or remove product categories. Discounts per category are managed in <strong>Set Discount → Category Discounts</strong>.
                </p>

                {/* ── Search ── */}
                <div className="stock-search-bar" style={{ marginBottom: 18 }}>
                    <input
                        type="text"
                        className="stock-search-input"
                        placeholder="Search categories…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                        <button className="stock-clear-search" onClick={() => setSearch("")}>✕</button>
                    )}
                </div>

                {/* ── Table ── */}
                <div className="table-scroll-wrapper">
                    <table className="products-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Category Name</th>
                                <th>Type</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginated.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="no-results">
                                        {search ? "No categories match your search." : "No categories found."}
                                    </td>
                                </tr>
                            ) : (
                                paginated.map((item, idx) => (
                                    <tr key={item.name}>
                                        <td style={{ color: "#aaa", fontSize: 13 }}>
                                            {(currentPage - 1) * pageSize + idx + 1}
                                        </td>
                                        <td className="product-name-cell">
                                            {editingCategory === item.name ? (
                                                <input
                                                    type="text"
                                                    className="spd-number-input"
                                                    value={editCategoryName}
                                                    onChange={(e) => setEditCategoryName(e.target.value)}
                                                    onBlur={requestSaveEdit}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") requestSaveEdit();
                                                        if (e.key === "Escape") setEditingCategory(null);
                                                    }}
                                                    autoFocus
                                                />
                                            ) : (
                                                item.name
                                            )}
                                        </td>
                                        <td>
                                            <BehaviorBadge behavior={item.behavior} />
                                        </td>
                                        <td>
                                            <div className="stock-action-btns">
                                                <button
                                                    className="spd-select-btn"
                                                    onClick={() => startEdit(item.name)}
                                                    disabled={isProcessing}
                                                >
                                                    <i className="fa-solid fa-pen" style={{ marginRight: 5 }}></i>Rename
                                                </button>
                                                <button
                                                    className="spd-clear-btn"
                                                    onClick={() => handleDeleteCategory(item.name)}
                                                    disabled={isProcessing}
                                                >
                                                    <i className="fa-solid fa-trash" style={{ marginRight: 5 }}></i>Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* ── Pagination ── */}
                {filteredRaw.length > 0 && (
                    <div className="pagination-bar" style={{ marginTop: "16px" }}>
                        <div className="pagination-info">
                            Showing {Math.min((currentPage - 1) * pageSize + 1, filteredRaw.length)}–
                            {Math.min(currentPage * pageSize, filteredRaw.length)} of {filteredRaw.length}
                        </div>
                        <div className="pagination-controls">
                            <button className="page-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>«</button>
                            <button className="page-btn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>‹</button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                                .reduce((acc, p, i, arr) => {
                                    if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
                                    acc.push(p);
                                    return acc;
                                }, [])
                                .map((item, i) =>
                                    item === "..." ? (
                                        <span key={`e-${i}`} className="page-ellipsis">…</span>
                                    ) : (
                                        <button
                                            key={item}
                                            className={`page-btn${currentPage === item ? " active" : ""}`}
                                            onClick={() => setCurrentPage(item)}
                                        >
                                            {item}
                                        </button>
                                    )
                                )}
                            <button className="page-btn" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>›</button>
                            <button className="page-btn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>»</button>
                        </div>
                        <div className="pagination-size">
                            <label>Rows per page:</label>
                            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
                                {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManageCategories;
